// react
import { ChangeEvent } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Button, Grid } from '@mui/material';

// icons
import { AddOutlined as IconAdd, DeleteOutlined as IconDelete } from '@mui/icons-material';

// types
import { ContractAmountPayload, ContractPayload } from '../../../@types';

// fields
import { Input } from '../../fields/Input/Input';
import { SelectCurrency } from '../../fields/SelectCurrency/SelectCurrency';

// props
export interface AmountsMultiCurrencyProps {
	amounts: ContractPayload['amounts'];
	onChange: (amounts: ContractPayload['amounts']) => void;
	idForm: string;
}

export const AmountsMultiCurrency = ({
	amounts = [],
	onChange,
	idForm,
}: AmountsMultiCurrencyProps) => {
	// hooks
	const { t } = useTranslation();

	// handlers
	const handleAddAmount = () => {
		const newAmount: ContractAmountPayload = {
			amount: 0,
			currency: '',
		};
		onChange([...amounts, newAmount]);
	};

	const handleRemoveAmount = (index: number) => {
		const newAmounts = amounts.filter((_, i) => i !== index);
		onChange(newAmounts);
	};

	const handleAmountChange = (
		index: number,
		field: 'amount' | 'currency',
		value: string | number
	) => {
		const newAmounts = [...amounts];
		newAmounts[index] = {
			...newAmounts[index],
			[field]: field === 'amount' ? Number(value) : value,
		};
		onChange(newAmounts);
	};

	return (
		<Grid container spacing={2}>
			{amounts.map((amount, index) => (
				<Grid item key={index} xs={12}>
					<Grid alignItems="flex-end" container spacing={2}>
						<Grid item xs={12} md={true}>
							<Input
								aria-label={index !== 0 ? t('amount') : undefined}
								id={`${idForm}-amount-${index}`}
								label={index === 0 ? t('amount') : undefined}
								name={`amount-${index}`}
								onChange={(e: ChangeEvent<HTMLInputElement>) =>
									handleAmountChange(index, 'amount', e.target.value)
								}
								required={true}
								type="number"
								value={amount.amount || ''}
							/>
						</Grid>
						<Grid item xs={12} md={true}>
							<SelectCurrency
								aria-label={index !== 0 ? t('currency') : undefined}
								disabledCurrencies={amounts
									.map((a) => a.currency)
									.filter((currency) => Boolean(currency))}
								id={`${idForm}-currency-${index}`}
								label={index === 0 ? t('currency') : undefined}
								name={`currency-${index}`}
								onChange={(e) => handleAmountChange(index, 'currency', e.target.value as string)}
								required={true}
								value={amount.currency || ''}
							/>
						</Grid>
						<Grid item xs={12} md="auto">
							<Button
								onClick={() => handleRemoveAmount(index)}
								color="error"
								disabled={amounts.length <= 1}
								variant="outlined"
								className="icon"
								aria-label={t('remove')}>
								<IconDelete />
							</Button>
						</Grid>
					</Grid>
				</Grid>
			))}

			<Grid item xs={12}>
				<Button
					onClick={handleAddAmount}
					color="primary"
					variant="outlined"
					fullWidth
					startIcon={<IconAdd />}>
					{t('addAmount')}
				</Button>
			</Grid>
		</Grid>
	);
};
